import { useRef } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import grey from '@mui/material/colors/grey';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { validateFiles } from './utils';
import { formatBytes } from '../../../utils/utils';
import Text from '../text/Text';

// TODO: Micah, at some point try and use this to replace the regular FileUpload component.
const FileUploadInput = ({
  countLimit,
  fileList,
  instructionText,
  onSetFiles,
  sizeLimit,
  typeLimit,
}) => {
  const fileRef = useRef();
  const { t } = useTranslation();

  const handleAddFiles = () => {
    if (fileList.length >= countLimit) return;
    const newFiles = Array.from(fileRef.current.files);
    const validatedFiles = validateFiles(newFiles, sizeLimit, typeLimit);
    onSetFiles(validatedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const target = event.dataTransfer.files;
    fileRef.current.files = target;
    handleAddFiles();
  };

  const removeFile = (fileIndex) => {
    const newFiles = [...fileList];
    newFiles.splice(fileIndex, 1);
    onSetFiles(newFiles);
  };

  const handleClick = () => fileRef.current.click();

  return fileList.length > 0 ? (
    <>
      <Text bodyBold color="textSecondary">
        {t('selectedFile', { ns: 'instance' })}
      </Text>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('fileName', { ns: 'general' })}</TableCell>
            <TableCell>{t('fileSize', { ns: 'general' })}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {fileList.map((file, i) => (
            <TableRow key={i}>
              <TableCell>{file.name}</TableCell>
              <TableCell>{formatBytes(file.size)}</TableCell>
              <TableCell>
                <IconButton
                  edge="end"
                  aria-label="remove file"
                  onClick={() => removeFile(i)}
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  ) : (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: grey[100],
        outline: '2px dashed gray',
        borderRadius: '5px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        outlineOffset: '-10px',
        p: 6,
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <Text>{instructionText}</Text>
      <Text caption>{t('upload.or', { ns: 'fields' })}</Text>
      <Button
        onClick={handleClick}
        variant="outlined"
        sx={{ bgcolor: 'white' }}
      >
        {t('upload.chooseFile', { ns: 'fields' })}
      </Button>
      <Text caption component="p">
        {t('sizeLimit', {
          ns: 'validations',
          size: formatBytes(sizeLimit, 0),
        })}
      </Text>
      <Text caption component="p">
        {t('fileExcluded', {
          ns: 'validations',
          size: formatBytes(sizeLimit, 0),
        })}
      </Text>
      <input
        data-testid="file-input"
        type="file"
        ref={fileRef}
        hidden
        multiple
        onChange={handleAddFiles}
      />
    </Box>
  );
};

FileUploadInput.propTypes = {
  countLimit: PropTypes.number.isRequired,
  fileList: PropTypes.array.isRequired,
  instructionText: PropTypes.string.isRequired,
  onSetFiles: PropTypes.func.isRequired,
  sizeLimit: PropTypes.number.isRequired,
  typeLimit: PropTypes.array,
};

export default FileUploadInput;
